import {
  CompanyTimeSeriesResponse,
  GetAllCompaniesResponse,
  GetCompaniesStatsResponse,
  GetCompanyByIdResponse,
  GetCompanyStatsResponse,
  TimeSeriesGranularity,
  UpdateCompanyRequest,
} from '../responseTypes/company';
import { baseApi } from './BaseService';
import {
  BillingAccountRequest,
  GetBillingAccountByIdResponse,
} from '../responseTypes/billingAccounts';
import { IntegrationBinding, IntegrationBindingTarget } from '../../shared/types/integrationConfiguration';

const baseUrl = 'admin/companies';
const billingUrl = 'admin/billing_accounts';
const attachedRoles = 'admin/attached_roles';

export const companiesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAllCompanies: build.query<GetAllCompaniesResponse, { query?: string }>({
      query: ({ query = '' }) => ({
        url: baseUrl,
        params: {
          query,
        },
      }),
    }),
    searchCompanies: build.query<GetAllCompaniesResponse, { query: string }>({
      query: ({ query }) => ({
        url: `${baseUrl}/email/search`,
        params: {
          query,
        },
      }),
    }),
    getCompanyById: build.query<GetCompanyByIdResponse, { companyId: string }>({
      query: ({ companyId }) => ({
        url: `${baseUrl}/${companyId}`,
      }),
      providesTags: ['Company'],
    }),
    getCompanyAttachedRoles: build.query<
      { attachedRoles: IntegrationBinding[] },
      { companyId: string }
    >({
      query: ({ companyId }) => ({
        url: `${attachedRoles}/${companyId}/type/${IntegrationBindingTarget.COMPANY}`,
      }),
      // All three attached-role mutations already invalidate Company.
      providesTags: ['Company'],
    }),
    getCompaniesStats: build.query<
      GetCompaniesStatsResponse,
      {
        limit?: number;
        offset?: number;
        query?: string;
        fromDate?: string;
        toDate?: string;
      }
    >({
      query: ({ limit = 20, offset = 0, query = '', fromDate, toDate }) => ({
        url: `${baseUrl}/stats`,
        params: {
          limit,
          offset,
          query,
          ...(fromDate !== undefined && toDate !== undefined
            ? { fromDate, toDate }
            : {}),
        },
      }),
    }),
    getCompanyStats: build.query<
      GetCompanyStatsResponse,
      { companyId: string }
    >({
      query: ({ companyId }) => ({
        url: `${baseUrl}/${companyId}/stats`,
      }),
    }),
    getCompanyStatsTimeseries: build.query<
      CompanyTimeSeriesResponse,
      {
        companyId: string;
        fromDate: string;
        toDate: string;
        granularity?: TimeSeriesGranularity;
        metrics?: string;
      }
    >({
      query: ({ companyId, fromDate, toDate, granularity, metrics }) => ({
        url: `${baseUrl}/${companyId}/stats/timeseries`,
        params: {
          fromDate,
          toDate,
          ...(granularity ? { granularity } : {}),
          ...(metrics ? { metrics } : {}),
        },
      }),
    }),
    updateCompanyById: build.mutation<
      GetCompanyByIdResponse,
      { companyId: string; data: UpdateCompanyRequest }
    >({
      query: ({ companyId, data }) => ({
        url: `${baseUrl}/${companyId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Company'],
    }),
    deleteCompanyById: build.mutation<string, { companyId: string }>({
      query: ({ companyId }) => ({
        url: `${baseUrl}/${companyId}`,
        method: 'DELETE',
      }),
    }),
    getCompanyBillingAccount: build.query<
      GetBillingAccountByIdResponse,
      { companyId: string }
    >({
      query: ({ companyId }) => ({
        url: `${billingUrl}/company/${companyId}`,
      }),
      providesTags: ['BillingAccount'],
    }),
    createBillingAccount: build.mutation<
      GetBillingAccountByIdResponse,
      { data: BillingAccountRequest }
    >({
      query: ({ data }) => ({
        url: `${billingUrl}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingAccount', 'Company'],
    }),
    updateBillingAccountById: build.mutation<
      GetBillingAccountByIdResponse,
      { accountId: string; data: BillingAccountRequest }
    >({
      query: ({ accountId, data }) => ({
        url: `${billingUrl}/${accountId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['BillingAccount', 'Company'],
    }),
    deleteBillingAccountById: build.mutation<string, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `${billingUrl}/${accountId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BillingAccount', 'Company'],
    }),
    attachIntegrationBinding: build.mutation<
      { attachedRole: IntegrationBinding },
      {
        companyId?: string;
        profileId?: string;
        roleId: string;
        type: IntegrationBindingTarget;
        customAttributes: Record<string, string>;
      }
    >({
      query: (data) => ({
        url: attachedRoles,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Company', 'Profile'],
    }),
    updateIntegrationBinding: build.mutation<
      { attachedRole: IntegrationBinding },
      {
        attachedRoleId: string;
        customAttributes: Record<string, string>;
      }
    >({
      query: ({ attachedRoleId, customAttributes }) => ({
        url: `${attachedRoles}/${attachedRoleId}`,
        method: 'PUT',
        body: { customAttributes },
      }),
      invalidatesTags: ['Company', 'Profile'],
    }),
    detachIntegrationBinding: build.mutation<
      string,
      {
        attachedRoleId: string;
      }
    >({
      query: ({ attachedRoleId }) => ({
        url: `${attachedRoles}/${attachedRoleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Company', 'Profile'],
    }),
  }),
});
